<template>
  <div v-if="(data && data.length) || showEmptyTable">
    <Loader :show="searched" :fixed="false" style="z-index: 5;" />
    <div
      :class="`tab-contents d-flex flex-row${
        reverse ? '-reverse' : ''
      } justify-content-between`"
    >
      <div
        v-if="(data && data.length) || !showEmptyTable"
        :class="chartsCollapsed ? 'table-container-expanded' : 'table-container'"
        ref="table-parent"
      >
        <div class="list-filter-wrapper">
          <button class="select-all-button" @click="selectOrDeselectAll()">
            {{ buttonText }}
          </button>
          <el-input
            placeholder="Search query"
            v-model="listFilter"
            clearable
          ></el-input>
          <div class="metric-threshold" v-if="metricThresholdOptions.length">
            <label class="metric-threshold__label">Min metric %</label>
            <el-select
              class="select-group-item metric-threshold__select"
              v-model="metricThreshold.metric"
              :placeholder="`Select metric`"
              multiple
              collapse-tags
              filterable
              clearable
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="option in metricThresholdOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                >
                  <span style="float: left">
                    {{ option.label }}
                  </span>
                </el-option>
              </el-option-group>
            </el-select>
            <el-input-number
              v-model="metricThreshold.value"
              class="metric-threshold__input"
              :min="0"
              :max="100"
              :step="1"
              :controls="false"
              placeholder="Min %"
            />
            <button class="select-all-button metric-threshold__clear" @click="clearMetricThreshold">Clear</button>
          </div>
          <button class="expand-button" @click="expandTable()">
            {{ chartsCollapsed ? 'Expand' : 'Collapse' }}
          </button>
        </div>
        <div class="calculate-confidence-wrapper">
          <el-tooltip v-if="!confidence" content="You need to check at least one metric to calculate significance.">
            <button
              disabled
              class="select-all-button"
              @click="calculateConfidence()"
            >
              Calculate
            </button>
          </el-tooltip>
          <button
            v-else
            class="select-all-button"
            @click="calculateConfidence()"
          >
            Calculate
          </button>
          <el-tooltip v-if="!confidence" content="You need to check at least one metric to calculate significance.">
            <el-select
              :disabled="!confidence"
              class="select-calculations"
              v-model="confidenceOption"
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="(option, index) in confidenceCalculateOptions"
                  :key="index"
                  :label="option.label"
                  :value="option.value"
                >
                  <span style="float: left">
                    {{option.label}}
                  </span>
                </el-option>
              </el-option-group>
            </el-select>
          </el-tooltip>
          <el-select
              v-else
              class="select-calculations"
              v-model="confidenceOption"
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="(option, index) in confidenceCalculateOptions"
                  :key="index"
                  :label="option.label"
                  :value="option.value"
                >
                  <span style="float: left">
                    {{option.label}}
                  </span>
                </el-option>
              </el-option-group>
            </el-select>
        </div>
        <div class="fragrances-container">
          <div
            class="fragrances-wrapper"
            :style="getFragrancesWrapperStyle()"
            ref="fragrances-wrapper"
          >
            <div class="d-flex align-items-center">
              <div class="tag-group" style="margin-top: 10px; margin-bottom: 10px;" v-if="confidence">
                <el-badge
                  v-for="(filter, index) in confidenceFilters"
                  :value="getBadgeValue(filter.value)"
                  :key="index"
                  type="info"
                  :style="{'cursor': 'pointer','margin-right': '20px', 'color': filter.color}"
                >
                  <el-tag
                    :key="index"
                    effect="dark"
                    :style="{ 'background-color': filter.color, border: 0 }"
                    v-model="confidenceFilter"
                    :value="filter.value"
                    @click="setConfidenceFilter(filter.value)"
                  >
                    <p style="color: white">
                      {{ filter.label }}
                    </p>
                  </el-tag>
                </el-badge>
              </div>
            </div>
            <div
              class="
                data data-hide data-header
                d-flex
                flex-row
                justify-content-between
                align-items-center
              "
            >
              <div
                class="d-flex align-items-center fragrance-name fragrance-name-header"
                :style="fragranceColumnStyle"
              >
                <span>{{ searchType === "concepts" ? "Concept" : "Fragrance" }} name</span>
                <div
                  class="fragrance-column-resizer"
                  aria-label="Resize fragrance name column"
                  aria-orientation="vertical"
                  role="separator"
                  title="Drag to resize column"
                  @pointerdown.prevent="startFragranceResize"
                ></div>
              </div>
              <div
                class="
                  attribute-values
                  d-flex
                  justify-content-between
                  align-items-center
                "
              >
                <div
                  class="metric-value"
                  v-for="metric in metricTypes"
                  :ref="`${metric.value}-${metric.aggregation}`"
                  :key="`${metric.value}-${metric.aggregation}`"
                  v-b-tooltip:hover.html="
                    `${formatChartLabel(metric.value)}<br>${formatChartLabel(
                      metric.aggregation
                    )}`
                  "
                >
                  {{
                    `[${parseAggregationType(
                      metric.aggregation
                    )}] ${formatChartLabel(metric.value)}`
                  }}
                </div>
              </div>
            </div>
            <RecycleScroller
              :key="`study-table-${forceRerender}`"
              class="scroller"
              :items="filteredData"
              :item-size="40"
              keyField="id"
              :emit-update="true"
              v-slot="{ item, index }"
            >
              <div
                class="
                  data data-hide
                  d-flex
                  flex-row
                  justify-content-between
                  align-items-center
                "
              >
                <div
                  class="d-flex align-items-start fragrance-name"
                  :style="fragranceColumnStyle"
                  v-if="item.prod"
                >
                  <b-tooltip
                    class="checkbox-tooltip"
                    :target="`checkbox-tooltip-${fragUniqueId(item)}`"
                    placement="right"
                  >
                    {{ formatValue(item.c_mi_number) }}
                    <br />
                    {{ formatValue(item.category) }}
                    <br />
                    Stage: {{ formatValue(item.stage) }}
                    <br />
                    {{ item.name || item.prod }}
                  </b-tooltip>
                  <Checkbox
                    :color="'purple'"
                    style="width: 100%"
                    :id="`checkbox-tooltip-${fragUniqueId(item)}`"
                    :name="fragUniqueId(item)"
                    :selected="selfFilters.selectedProducts"
                    :value="selfFilters.selectedProducts[fragUniqueId(item)]"
                    :link="true"
                    @change="(val) => (selectOne(fragUniqueId(item), val))"
                  >
                    <span>
                        {{ item.name || item.prod }}
                        <small v-if="item.stage"> — {{ formatValue(item.stage) }}</small>
                    </span>
                  </Checkbox>
                  <router-link
                    style="margin-left: 5px;"
                    :to="{
                      name:
                        searchType === 'concepts'
                          ? 'concept-profile'
                          : 'fragrance-profile',
                      params: { id: item.study_id, code: item.prod },
                      query: {
                        attributes: JSON.stringify(
                          metricTypes.map((el) => el.value)
                        )
                      },
                    }"
                    target="_blank"
                  >
                    <font-awesome-icon icon="external-link-alt" />
                  </router-link>
                </div>
                <span
                  v-else
                  class="d-flex align-items-start fragrance-name"
                  :style="fragranceColumnStyle"
                >
                  {{ formatValue(item.product) }}
                </span>
                <div
                  class="
                    attribute-values
                    d-flex
                    justify-content-between
                    align-items-center
                  "
                >
                  <div
                    class="
                      metric-value
                      d-flex
                      justify-content-center
                      align-items-center
                    "
                    :style="getWidth(metric)"
                    v-for="metric in metricTypes"
                    :key="`${metric.value}–${metric.aggregation}-${index}`"
                  >
                    {{ getMetricValue(item, metric) }}
                    <el-tag
                        effect="plain"
                        type="success"
                        :style="{ color: getConfidenceColor(getConfidenceValue(item, metric)), border: 0 }"
                        v-if="checkConfidenceDisplay(item, metric)"
                    >
                      <i :class="getConfidenceIcon(getConfidenceValue(item, metric))"></i>
                      {{ parseConfidenceValue(getConfidenceValue(item, metric)) }}
                    </el-tag>
                  </div>
                </div>
              </div>
            </RecycleScroller>
          </div>
        </div>
      </div>
      <div
        v-else
        :class="`tab-contents d-flex flex-row${
          reverse ? '-reverse' : ''
        } justify-content-between`"
      >
        <span
          class="data data-header align-items-center"
        >
          No data found after filtering. Please change filters and try again.
        </span>
      </div>
      <div class="results-box" v-if="!chartsCollapsed">
        <div class="results-header">
          <div class="results-header__title">Analytics Results</div>
          <img
            src="../../../assets/images/icons/excel-export-H.svg"
            class="dwn-button"
            @click="download('metrics', chosenXlsxFilters, metricThresholdPayload)"
          />
          <el-select
            class="select-group-item"
            :placeholder="`Filter results`"
            filterable
            collapse-tags
            multiple
            v-model="chosenXlsxFilters"
          >
            <el-option-group label="Press Tab/Escape to exit">
              <el-option
                v-for="option in xlsxFilters"
                :key="option.value"
                :value="option.value"
                :label="option.label"
              >
                <span style="float: left">
                  {{option.label}}
                </span>
              </el-option>
            </el-option-group>
          </el-select>
        </div>

        <div class="charts-container d-flex flex-column align-items-center">
          <template v-if="chartData.length && hasRDF && !isIRT">
            <HorizontalAnalyticsChart
              v-for="item in chartData"
              :key="`chart-${item.key}`"
              :style="`order: ${getChartOrder(item.key)}`"
              :data="item.data"
              :mean="item.mean"
              :n-values="item.counts"
            ></HorizontalAnalyticsChart>
          </template>

          <template v-if="summary && Object.keys(summary) && hasGenisys && !isIRT">
            <BarChart
              v-for="(item, idx) in genisysChartData"
              :key="`chart-${idx}`"
              :chart-data="item"
              :options="genisysChart.options"
              :styles="{height: '300px', width: '100%', position: 'relative'}"
              :data-types="{ Count: 'value' }"
            ></BarChart>
          </template>
        </div>

      </div>
    </div>
  </div>
  <div v-else>
    <div class="no-data d-flex flex-column align-items-center">
      <div
        v-if="ready.metrics"
        class="h-100 d-flex justify-content-center align-items-center"
      >
        <i>No data</i>
      </div>
    </div>
  </div>
</template>

<script>
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import { mapActions, mapGetters } from 'vuex'
import generalActions from '../../../store/modules/general/actionTypes'
import studiesActions from '../../../store/modules/studies/actionTypes'
import studiesGetters from '../../../store/modules/studies/getterTypes'
import tooltips from '../../../constants/tooltips'
import _ from 'lodash'
import HorizontalAnalyticsChart from '../../shared/HorizontalAnalyticsChart'
import BarChart from '../../shared/BarChart'
import Checkbox from '../../shared/Checkbox'
import searchFunctions from '../../../helpers/searchFunctions'
import Loader from '../../shared/Loader'

export default {
  name: 'FragranceMetricsTable',
  components: { Loader, Checkbox, HorizontalAnalyticsChart, BarChart },
  props: {
    data: {
      type: Array,
      required: false
    },
    summary: {
      type: Object,
      required: false
    },
    fileType: String,
    searchType: String,
    filterFields: Object,
    download: Function,
    ready: Object,
    reverse: Boolean,
    fields: Array,
    isGenisys: Boolean
  },
  data () {
    return {
      showEmptyTable: false,
      forceRerender: 0,
      fragranceColumnWidth: 220,
      fragranceColumnMinWidth: 180,
      fragranceColumnResize: {
        active: false,
        startX: 0,
        startWidth: 220
      },
      buttonText: 'Deselect all',
      sortBy: {
        key: 'category',
        desc: false
      },
      metricThreshold: {
        metric: [],
        value: null
      },
      selfFilters: {
        selectedProducts: {},
        isFiltered: false
      },
      xlsxFilters: [],
      chosenXlsxFilters: [],
      pagination: {
        page: 1,
        perPage: 20
      },
      listFilter: '',
      fileQuery: '',
      searched: false,
      filterTimer: null,
      yearObject: {
        min: 18,
        max: 64
      },
      intervals: {
        metrics: '',
        significance: ''
      },
      tooltips,
      confidenceFilters: [
        { type: 'info', label: 'All', value: undefined, color: 'grey' },
        { type: 'success', label: '99 %', value: { level: 0.01, table: 'mt' }, color: '#67c23a' },
        { type: '', label: '95 %', value: { level: 0.05, table: 'mt' }, color: '#95d475' },
        { type: 'danger', label: '90 %', value: { level: 0.1, table: 'mt' }, color: '#c2e7b0' },
        { type: 'success', label: '99 %', value: { level: 0.01, table: 'let' }, color: '#f56c6c' },
        { type: '', label: '95 %', value: { level: 0.05, table: 'let' }, color: '#f89898' },
        { type: 'danger', label: '90 %', value: { level: 0.1, table: 'let' }, color: '#fbc4c4' }
      ],
      confidenceExamples: [
        { value: 99, color: '#67c23a' },
        { value: 95, color: '#95d475' },
        { value: 90, color: '#c2e7b0' },
        { value: 99, color: '#f56c6c' },
        { value: 95, color: '#f89898' },
        { value: 90, color: '#fbc4c4' }
      ],
      confidenceCalculateOptions: [
        { value: 'avg', label: 'Selection vs Average' },
        { value: 'rest', label: 'Selection vs Average of the rest' }
      ],
      confidenceFilter: undefined,
      confidenceOption: 'avg',
      confidenceValue: 0,
      chartsCollapsed: false,
      chart: {
        options: {
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false
                }
              }
            ],
            yAxes: [
              {
                ticks: {
                  min: 0
                }
              }
            ]
          },
          barValueSpacing: 20,
          responsive: true,
          maintainAspectRatio: false
        }
      },
      genisysChart: {
        options: {
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false
                }
              }
            ],
            yAxes: [
              {
                ticks: {
                  min: 0,
                  callback: function (val) {
                    return val % 1 === 0 ? val : null
                  }
                }
              }
            ]
          },
          tooltips: {
            callbacks: {
              label: function (tooltipItem, data) {
                let label = data.datasets[tooltipItem.datasetIndex].label || ''

                let value = Number(data.datasets[tooltipItem.datasetIndex].data[
                  tooltipItem.index
                ])
                if (isNaN(value)) {
                  value = 0
                }
                label = label.split('.')[0]
                return label + ': ' + `${value.toFixed()}`
              },
              title: function (tooltipItem, data) {
                let label = data.labels[tooltipItem[0].index]
                let nextLabel = data.labels[tooltipItem[0].index + 1]
                if (nextLabel) {
                  label += ' - ' + nextLabel
                } else {
                  const step = Number(data.labels[1]) - Number(data.labels[0])
                  label += ' - ' + (Number(label) + step).toFixed(2)
                }
                return label
              }
            }
          },
          responsive: true,
          maintainAspectRatio: false
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      filterValues: studiesGetters.GET_SEARCH_FILTERS,
      metricsJobId: studiesGetters.GET_METRICS_JOB_ID,
      significanceJobId: studiesGetters.GET_SIGNIFICANCE_JOB_ID,
      significanceResults: studiesGetters.GET_SIGNIFICANCE_RESULTS
    }),
    metricThresholdOptions () {
      return this.metricTypes.map(metric => {
        return {
          value: `${metric.value}.${metric.aggregation}`,
          label: `[${this.parseAggregationType(metric.aggregation)}] ${this.formatChartLabel(metric.value)}`
        }
      })
    },
    fragranceColumnStyle () {
      const width = this.fragranceColumnWidth || this.getDefaultFragranceColumnWidth()
      return {
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`
      }
    },
    allowedChartMetrics () {
      return _.uniq(
        this.metricTypes
          .filter(metric => metric.value && metric.value.indexOf('_') !== -1)
          .map(metric => metric.value)
          .concat(['liking', 'strength', 'purchase_intention'])
      )
    },
    filteredData () {
      let arrayCopy = []
      try {
        arrayCopy = this.formatData()
      } catch {
        return []
      }
      if (this.listFilter) {
        arrayCopy = arrayCopy.filter((el) =>
          el.name
            ? el.name.toLowerCase().includes(this.listFilter.toLowerCase())
            : (el.prod ? String(el.prod).includes(this.listFilter) : false)
        )
      }

      if (this.confidenceFilter !== undefined && this.confidence) {
        arrayCopy = arrayCopy.filter(el => {
          return this.metricTypes.some(metric => {
            const cEl = this.getConfidenceValue(el, metric)
            return cEl !== undefined &&
                cEl.value.level !== undefined &&
                cEl.value.table !== undefined &&
                cEl.value.level === this.confidenceFilter.level &&
                cEl.value.table === this.confidenceFilter.table
          })
        })
      }

      if (this.metricThresholdMetrics.length && this.metricThreshold.value !== null && this.metricThreshold.value !== '') {
        arrayCopy = arrayCopy.filter(el => {
          return this.metricThresholdMetrics.every(metric => {
            const metricValue = this.getRawMetricValue(el, metric)
            return metricValue !== null && metricValue >= Number(this.metricThreshold.value)
          })
        })
      }

      return arrayCopy
    },
    metricThresholdMetrics () {
      return Array.isArray(this.metricThreshold.metric)
        ? this.metricThreshold.metric
        : (this.metricThreshold.metric ? [this.metricThreshold.metric] : [])
    },
    metricThresholdPayload () {
      if (!this.metricThresholdMetrics.length || this.metricThreshold.value === null || this.metricThreshold.value === '') {
        return null
      }
      return {
        metrics: this.metricThresholdMetrics,
        value: this.metricThreshold.value
      }
    },
    paginatedData () {
      const { perPage, page } = this.pagination
      return this.filteredData.slice((page - 1) * perPage, page * perPage)
    },
    chartData () {
      const apiSummary = _.cloneDeep(this.summary) || {}
      const fallbackSummary = this.buildSummaryFromResults(this.allowedChartMetrics)

      return this.allowedChartMetrics
        .map(metric => {
          const metricSummary = this.hasRenderableMetricSummary(apiSummary[metric])
            ? apiSummary[metric]
            : fallbackSummary[metric]
          return this.buildChartDataForMetric(metric, metricSummary)
        })
        .filter(Boolean)
    },
    genisysChartData () {
      var self = this
      var metrics = this.metricTypes
        .filter(function (el) {
          return ['hfd_hedonic', 'addiction', 'cogproc', 'attention', 'relaxation', 'energizing'].indexOf(el.value) !== -1
        })
        .map(function (el) { return el.value + '.' + el.aggregation })

      var data = metrics.map(function (metric) {
        var vals = self.filteredData
          .map(function (row) { return row ? row[metric] : null })
          .filter(function (x) { return x !== null && typeof x !== 'undefined' })
          .map(function (x) { return self.parseChartNumber(x) })
          .filter(function (x) { return Number.isFinite(x) })
          .sort(function (a, b) { return a - b })
        return { metric: metric, values: vals }
      })

      var divisions = data.map(function (el) {
        if (!el.values.length) {
          return { metric: el.metric, step: 1, divisions: [0, 1, 2, 3, 4, 5, 6, 7] }
        }
        var min = Math.min.apply(null, el.values)
        var max = Math.max.apply(null, el.values)
        var range = max - min
        var step = range === 0 ? 1 : range / 8.0
        var divs = []
        for (var i = 0; i < 8; i++) divs.push(min + step * i)
        return { metric: el.metric, step: step, divisions: divs }
      })

      var counts = divisions.map(function (el, idx) {
        var vals = el.divisions.map(function (div, i) {
          var upper = div + el.step
          var cnt = data[idx].values.filter(function (v) {
            return (v >= div && (i < el.divisions.length - 1 ? v < upper : v <= upper))
          }).length
          return { div: div, count: cnt }
        })
        return { metric: el.metric, values: vals }
      })

      var chartData = {}
      counts.forEach(function (el) {
        var labels = el.values.map(function (v) { return Number(v.div).toFixed(2) })
        var ds = el.values.map(function (v) { return v.count })
        chartData[el.metric] = {
          labels: labels,
          datasets: [
            {
              label: self.formatChartLabel(el.metric),
              backgroundColor: 'rgba(163,160,251,0.5)',
              data: ds
            }
          ]
        }
      })
      return chartData
    },
    metricTypes () {
      return this.filterValues.metrics.metricsList.length
        ? this.filterValues.metrics.metricsList.map((el) => {
          const valueSplit = el.metric.value.split('_')
          return {
            value:
                valueSplit.length > 1
                  ? valueSplit.join('_')
                  : el.metric.value,
            aggregation: el.aggregation.value,
            confidence: el.confidence.value
          }
        })
        : []
    },
    hasGenisys () {
      return !!this.metricTypes.find((el) =>
        ['hfd_hedonic',
          'addiction',
          'cogproc',
          'attention',
          'relaxation',
          'energizing'].includes(el.value)
      )
    },
    hasRDF () {
      return !!this.metricTypes.find((el) =>
        ['liking',
          'strength',
          'purchase_intention'].includes(el.value) || el.value.includes('_')
      )
    },
    confidence () {
      return this.metricTypes.some(metric => metric.confidence)
    },
    isIRT () {
      const irtMetrics = ['irt_yes', 'irt_no', 'hi_yes', 'hi_no']
      const containsIRTAgg = (element) => irtMetrics.includes(element.aggregation)
      return this.metricTypes.some(containsIRTAgg)
    }
  },
  methods: {
    ...mapActions({
      getStudyFiles: studiesActions.FETCH_FILES,
      queueAnalyticSearch: studiesActions.QUEUE_METRIC_SEARCH,
      getMetrics: studiesActions.CHECK_METRIC_JOB_STATUS,
      fetchXlsxFilters: studiesActions.FETCH_FRAGRANCE_XLSX_FILTERS,
      queueSignificanceCalculation: studiesActions.QUEUE_SIGNIFICANCE_CALCULATION,
      getSignificance: studiesActions.CHECK_SIGNIFICANCE_JOB_STATUS,
      toggleLoading: generalActions.TOGGLE_LOADING
    }),
    fragUniqueId (frag) {
      const sid = frag.study_id || frag.id
      const stage = (frag.stage_key !== undefined && frag.stage_key !== null)
        ? String(frag.stage_key)
        : (frag.stage ? String(frag.stage) : '')
      // keep base id parsing intact for backend by using "__" for stage suffix
      return `${sid}-${frag.c_mi_number}-${frag.prod}__${stage}`
    },
    getDefaultFragranceColumnWidth () {
      return window.innerWidth > 1400 ? 300 : 220
    },
    getTableParentElement () {
      const parentElement = this.$refs['table-parent']
      return Array.isArray(parentElement) ? parentElement[0] : parentElement
    },
    getFragrancesWrapperElement () {
      const wrapperElement = this.$refs['fragrances-wrapper']
      return Array.isArray(wrapperElement) ? wrapperElement[0] : wrapperElement
    },
    getFragrancesWrapperStyle () {
      const calculatedWidth =
        (this.fragranceColumnWidth || this.getDefaultFragranceColumnWidth()) +
        150 * this.metricTypes.length +
        20
      return {
        width: `${calculatedWidth}px`,
        minWidth: '100%'
      }
    },
    getMinimumMetricColumnsWidth () {
      return this.metricTypes.length * 100
    },
    getMaxFragranceColumnWidth () {
      const wrapperElement = this.getFragrancesWrapperElement()
      if (!wrapperElement) {
        return this.getDefaultFragranceColumnWidth()
      }

      const availableWidth =
        wrapperElement.clientWidth - this.getMinimumMetricColumnsWidth() - 20
      return Math.max(this.fragranceColumnMinWidth, availableWidth)
    },
    clampFragranceColumnWidth (width = this.fragranceColumnWidth) {
      const minWidth = this.fragranceColumnMinWidth
      const maxWidth = this.getMaxFragranceColumnWidth()
      this.fragranceColumnWidth = Math.min(Math.max(width, minWidth), maxWidth)
    },
    syncFragranceColumnWidth (width = this.fragranceColumnWidth || this.getDefaultFragranceColumnWidth()) {
      this.$nextTick(() => {
        this.clampFragranceColumnWidth(width)
      })
    },
    startFragranceResize (event) {
      this.stopFragranceResize()
      this.fragranceColumnResize = {
        active: true,
        startX: event.clientX,
        startWidth: this.fragranceColumnWidth
      }
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      window.addEventListener('pointermove', this.handleFragranceResize)
      window.addEventListener('pointerup', this.stopFragranceResize)
    },
    handleFragranceResize (event) {
      if (!this.fragranceColumnResize.active) return

      const nextWidth =
        this.fragranceColumnResize.startWidth + (event.clientX - this.fragranceColumnResize.startX)
      this.clampFragranceColumnWidth(nextWidth)
    },
    stopFragranceResize () {
      this.fragranceColumnResize.active = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      window.removeEventListener('pointermove', this.handleFragranceResize)
      window.removeEventListener('pointerup', this.stopFragranceResize)
    },
    handleWindowResize () {
      const previousWidth = this.fragranceColumnWidth
      this.syncFragranceColumnWidth(previousWidth || this.getDefaultFragranceColumnWidth())
    },
    sortByMultipleKeys (keys) {
      return (a, b) => {
        if (keys.length === 0) return 0
        const key = Object.keys(a).find((el) => el.includes(keys[0]))
        let firstVal =
          typeof a[key] !== 'number'
            ? a[key] && a[key].includes('%')
              ? Math.round(parseInt(a[key].slice(0, -1)))
              : 0
            : Math.round(a[key])
        let secondVal =
          typeof b[key] !== 'number'
            ? b[key] && b[key].includes('%')
              ? Math.round(parseInt(b[key].slice(0, -1)))
              : 0
            : Math.round(b[key])
        if (firstVal < secondVal) return 1
        else if (firstVal > secondVal) return -1
        else return this.sortByMultipleKeys(keys.slice(1))(a, b)
      }
    },
    parseChartNumber (value) {
      if (typeof value === 'number' && Number.isFinite(value)) return value
      if (value === null || value === undefined || value === '') return null
      if (typeof value === 'object') {
        if (Object.prototype.hasOwnProperty.call(value, 'value')) {
          return this.parseChartNumber(value.value)
        }
        return null
      }
      const parsed = Number(String(value).replace(/[^0-9.-]/g, ''))
      return Number.isFinite(parsed) ? parsed : null
    },
    normalizeChartAggregationKey (key) {
      const normalized = String(key || '').toLowerCase()
      if (!normalized || normalized.includes('.')) return ''
      if (
        normalized === 'top_2_box' ||
        normalized === 'top2_box' ||
        normalized === 'top2box' ||
        normalized === 'top_box'
      ) return 'top_2_box'
      if (
        normalized === 'just_right' ||
        normalized === 'jar' ||
        normalized === 'neutral'
      ) return 'just_right'
      if (
        normalized === 'bottom_2_box' ||
        normalized === 'bottom2_box' ||
        normalized === 'bottom2box' ||
        normalized === 'bottom_box'
      ) return 'bottom_2_box'
      return ''
    },
    getChartAggregationLabel (metric, aggregationKey) {
      if (metric === 'strength') {
        switch (aggregationKey) {
          case 'top_2_box':
            return 'Strong'
          case 'just_right':
            return 'Just right'
          case 'bottom_2_box':
            return 'Weak'
        }
      }

      if (aggregationKey === 'just_right') {
        return 'Neutral'
      }

      return this.formatChartLabel(String(aggregationKey))
    },
    hasRenderableMetricSummary (metricSummary) {
      if (!metricSummary || typeof metricSummary !== 'object' || Array.isArray(metricSummary)) return false

      return Object.entries(metricSummary).some(([key, value]) => {
        const normalizedKey = this.normalizeChartAggregationKey(key) ||
          (['mean', 'eeg_mean'].includes(String(key || '').toLowerCase()) ? 'mean' : '')
        const num = this.parseChartNumber(value)
        return Boolean(normalizedKey) && Number.isFinite(num) && num > 0
      })
    },
    buildChartDataForMetric (metric, metricSummary) {
      if (!metricSummary || typeof metricSummary !== 'object' || Array.isArray(metricSummary)) return null

      const entries = Object.keys(metricSummary).map(key => ({ key, value: metricSummary[key] }))
      const meanObj = entries.find(entry => entry && entry.key === 'mean') ||
        entries.find(entry => entry && entry.key === 'eeg_mean') ||
        null
      const mean = (meanObj && typeof meanObj.value !== 'undefined')
        ? this.parseChartNumber(meanObj.value)
        : null

      const bucketedValues = {}
      entries.forEach(entry => {
        if (!entry || typeof entry.key !== 'string') return
        const normalizedAggKey = this.normalizeChartAggregationKey(entry.key)
        if (!normalizedAggKey) return
        if (typeof bucketedValues[normalizedAggKey] === 'undefined') {
          bucketedValues[normalizedAggKey] = entry.value
        }
      })

      const distribution = ['top_2_box', 'just_right', 'bottom_2_box']
        .filter(aggregationKey => typeof bucketedValues[aggregationKey] !== 'undefined')
        .map(aggregationKey => {
          const value = this.parseChartNumber(bucketedValues[aggregationKey])
          return {
            key: aggregationKey,
            value: Number.isFinite(value) ? value : 0
          }
        })

      if (!distribution.length || !distribution.some(entry => entry.value > 0)) {
        return null
      }

      return {
        key: metric,
        mean: mean,
        counts: entries.filter(entry => entry && typeof entry.key === 'string' && entry.key.indexOf('.') !== -1),
        data: {
          labels: distribution.map(entry => this.getChartAggregationLabel(metric, entry.key)),
          datasets: [
            {
              label: this.formatChartLabel(metric),
              backgroundColor: 'rgba(163,160,251,0.5)',
              data: distribution.map(entry => entry.value)
            }
          ]
        }
      }
    },
    buildSummaryFromResults (allowedMetrics = []) {
      const rows = Array.isArray(this.data) ? this.data : []
      const summary = {}

      rows.forEach(row => {
        const rowWeight = this.parseChartNumber(row.count) || 1
        Object.keys(row || {}).forEach(key => {
          if (!key.includes('.')) return
          const [metric, ...aggParts] = key.split('.')
          const aggregation = aggParts.join('.')
          if (!metric || !aggregation) return
          if (allowedMetrics.length && !allowedMetrics.includes(metric)) return

          const numericValue = this.parseChartNumber(row[key])
          if (numericValue === null) return

          if (!summary[metric]) summary[metric] = {}
          if (aggregation.endsWith('.count')) {
            summary[metric][aggregation] = (summary[metric][aggregation] || 0) + numericValue
            return
          }

          const weightKey = `__${aggregation}__weight`
          const sumKey = `__${aggregation}__sum`
          summary[metric][sumKey] = (summary[metric][sumKey] || 0) + (numericValue * rowWeight)
          summary[metric][weightKey] = (summary[metric][weightKey] || 0) + rowWeight
        })
      })

      Object.keys(summary).forEach(metric => {
        Object.keys(summary[metric]).forEach(key => {
          if (!key.startsWith('__') || !key.endsWith('__sum')) return
          const aggregation = key.replace(/^__/, '').replace(/__sum$/, '')
          const weight = summary[metric][`__${aggregation}__weight`]
          summary[metric][aggregation] = weight ? summary[metric][key] / weight : 0
          delete summary[metric][key]
          delete summary[metric][`__${aggregation}__weight`]
        })
      })

      return summary
    },
    async filter () {
      if (this.intervals.metrics) {
        clearInterval(this.intervals.metrics)
      }
      this.searched = true
      const queryFilters = _.cloneDeep(this.filterValues)
      let selectedFragrancesId = Object.keys(this.selfFilters.selectedProducts).filter(
        key => this.selfFilters.selectedProducts[key] === true
      )
      let selectedFragrances = this.data.filter(ob => selectedFragrancesId.includes(
        this.fragUniqueId(ob)
      )).map(ob => ({
        id: ob.id,
        c_mi_number: ob.c_mi_number,
        prod: ob.prod
      })) || []
      await this.queueAnalyticSearch({
        q: {
          ...searchFunctions.parseDataForSearch(queryFilters),
          demographics_fragrances: [
            {
              field: 'age',
              filter: {
                type: 'range',
                from: this.yearObject.min,
                to: this.yearObject.max
              }
            }
          ],
          fragrances: selectedFragrances
        },
        data: null
      })

      this.selfFilters.isFiltered = true

      this.intervals.metrics = setInterval(async () => {
        const data = await this.getMetrics(this.metricsJobId)
        if (data) {
          clearInterval(this.intervals.metrics)
          this.searched = false
        }
      }, 3000)
    },
    queueFilterRefresh () {
      if (this.filterTimer) {
        clearTimeout(this.filterTimer)
      }
      this.filterTimer = setTimeout(() => {
        if (this.hasSomeFragrancesSelected()) {
          this.filter()
        }
      }, 250)
    },
    formatData () {
      const formattedArray = []
      this.data.forEach((el, idx) => {
        formattedArray.push({
          ...el,
          study_id: el.id,
          id: idx
        })
      })
      return formattedArray
    },
    formatValue (value) {
      if (!value || value === 'No Tag') {
        return '-'
      } else if (typeof value === 'string') {
        return value && value.charAt(0).toUpperCase() + value.slice(1)
      } else {
        return value
      }
    },
    formatChartLabel (value) {
      if (this.isGenisys) {
        value = value.replace('.mean', '')
      }
      if (value.includes('hfd_')) {
        value = value.replace('hfd_', '')
        return value.charAt(0).toUpperCase() + value.slice(1)
      } else if (value === 'cogproc') {
        return 'Cognitive Processing'
      } else {
        return value.charAt(0).toUpperCase() + value.slice(1).replace(/_+/g, ' ')
      }
    },
    changeSort (newKey) {
      if (this.sortBy.key === newKey) {
        const value = !this.sortBy.desc
        this.$set(this.sortBy, 'desc', value)
      } else {
        this.$set(this.sortBy, 'key', newKey)
        this.$set(this.sortBy, 'desc', false)
      }
    },
    hasSomeFragrancesSelected () {
      return Object.keys(this.selfFilters.selectedProducts).length && Object.values(this.selfFilters.selectedProducts).some(
        e => e === true
      )
    },
    setFragrancesButtonText () {
      this.buttonText = this.hasSomeFragrancesSelected() ? 'Deselect all' : 'Select all'
    },
    selectOne (uniqueId, val) {
      this.selfFilters.selectedProducts[uniqueId] = val
      this.setFragrancesButtonText()
      this.queueFilterRefresh()
    },
    selectOrDeselectAll () {
      if (this.hasSomeFragrancesSelected()) {
        this.data.forEach(fragrance => {
          this.selfFilters.selectedProducts[this.fragUniqueId(fragrance)] = false
        })
      } else {
        this.data.forEach(fragrance => {
          this.selfFilters.selectedProducts[this.fragUniqueId(fragrance)] = true
        })
      }
      this.setFragrancesButtonText()
      this.forceRerender += 1
      this.queueFilterRefresh()
    },
    getMetricValue (obj, metric) {
      const key = Object.keys(obj).find(
        (el) => el.includes(metric.value) && el.includes(metric.aggregation)
      )
      if (key) {
        const parsedValue = this.parseChartNumber(obj[key])
        if (!Number.isFinite(parsedValue)) return '-'
        const metricFilter = key.split('.')
        if (metricFilter[1] === 'mean') {
          return parsedValue.toFixed(2)
        } else {
          return `${Math.round(parsedValue)}%`
        }
      } else {
        return '-'
      }
    },
    getChartOrder (tag) {
      switch (tag) {
        case 'liking':
          return 2
        case 'strength':
          return 4
        case 'purchase_intention':
          return 3
        default:
          return 1
      }
    },
    getMetricLabel (metric) {
      const metricRange = this.filterFields.metrics_range
      const metricFilter = metric.split('.')
      if (metricFilter[1] === 'mean') {
        const range = `[${metricRange[metricFilter[0]][0]}-${
          metricRange[metricFilter[0]][1]
        }]`
        return `${metricFilter[0]}, ${metricFilter[1]} ${range}`.replace(
          /_+/g,
          ' '
        )
      }
      return `${metricFilter[0]}, ${metricFilter[1]} [%]`.replace(/_+/g, ' ')
    },
    parseAggregationType (type) {
      switch (type) {
        case 'top_box':
          return 'TB'
        case 'top_2_box':
          return 'T2B'
        case 'mean':
          return 'M'
        case 'bottom_box':
          return 'BB'
        case 'bottom_2_box':
          return 'B2B'
        case 'just_right':
          return 'JAR/N'
        case 'variance':
          return 'VAR'
        case 'hi_yes':
          return 'HiY'
        case 'hi_no':
          return 'HiN'
        case 'irt_yes':
          return 'YES'
        case 'irt_no':
          return 'NO'
        default:
          return ''
      }
    },
    nextConfidence () {
      this.confidenceValue = (this.confidenceValue + 1) % 6
    },
    expandTable () {
      this.chartsCollapsed = !this.chartsCollapsed
      this.$nextTick(() => {
        this.forceRerender += 1
        this.syncFragranceColumnWidth()
      })
    },
    clearMetricThreshold () {
      this.metricThreshold.metric = []
      this.metricThreshold.value = null
    },
    getWidth (metric) {
      var metricRef = metric.value + '-' + metric.aggregation
      var arr = this.$refs[metricRef]
      var px = '120'
      if (arr && arr[0] && arr[0].clientWidth) {
        px = String(arr[0].clientWidth)
      }
      return 'width: ' + px + 'px'
    },
    async calculateConfidence () {
      this.toggleLoading(true)
      const q = {
        calculation: this.confidenceOption,
        metrics: this.metricTypes.map(metric => {
          return {
            field: metric.value,
            aggregation: metric.aggregation,
            confidence: metric.confidence
          }
        })
      }
      const selected = this.selfFilters.selectedProducts
      const dataResults = this.data
      const response = await this.queueSignificanceCalculation({
        q: q,
        data: {
          selected: selected,
          data: dataResults
        }
      })
      this.significanceJobId = (response && response.jobId) ? response.jobId : null
      if (!this.significanceJobId) {
        console.error('No jobId returned from queueSignificanceCalculation!')
        this.toggleLoading(false)
        return
      }

      this.intervals.significance = setInterval(async () => {
        const status = await this.getSignificance({ jobId: this.significanceJobId })
        if (status) {
          clearInterval(this.intervals.significance)
          this.toggleLoading(false)
          this.searched = false
        }
      }, 3000)
    },
    getConfidenceValue (item, metric) {
      const itemid = this.fragUniqueId(item)
      const metricName = metric.value + '.' + metric.aggregation + '.confidence'
      const result = this.significanceResults.find(
        e => e.id === itemid
      )
      if (result) {
        return {
          id: result.id,
          value: result[metricName] ? result[metricName] : { level: undefined, value: undefined }
        }
      } else {
        return undefined
      }
    },
    getConfidenceColor (result) {
      if (result !== undefined) {
        if (result.value === '-') {
          return '#ccc'
        } else if (result.value.level === 0.01 && result.value.table === 'mt') {
          return '#67c23a'
        } else if (result.value.level === 0.05 && result.value.table === 'mt') {
          return '#95d475'
        } else if (result.value.level === 0.1 && result.value.table === 'mt') {
          return '#c2e7b0'
        } else if (result.value.level === 0.01 && result.value.table === 'let') {
          return '#f56c6c'
        } else if (result.value.level === 0.05 && result.value.table === 'let') {
          return '#f89898'
        } else if (result.value.level === 0.1 && result.value.table === 'let') {
          return '#fbc4c4'
        } else {
          return '#ccc'
        }
      } else {
        return '#ccc'
      }
    },
    getConfidenceIcon (result) {
      if (result !== undefined) {
        if (result.value === '-' || (result.value.level === undefined && result.value.table === undefined) || (result.value.level === '' && result.value.table === '')) {
          return 'el-icon-remove'
        } else {
          return 'el-icon-success'
        }
      }
    },
    parseConfidenceValue (result) {
      if (result !== undefined) {
        if (result.value.level === 0.01) {
          return 99
        } else if (result.value.level === 0.05) {
          return 95
        } else if (result.value.level === 0.1) {
          return 90
        }
      } else {
        return '-'
      }
    },
    setConfidenceFilter (value) {
      this.confidenceFilter = value
    },
    getBadgeValue (value) {
      const data = this.formatData().filter(el =>
        el.name
          ? el.name.toLowerCase().includes(this.listFilter.toLowerCase())
          : (el.prod ? String(el.prod).includes(this.listFilter) : false)
      )
      return value !== undefined ? data.filter(el => {
        return this.metricTypes.some(metric => {
          const cEl = this.getConfidenceValue(el, metric)
          return cEl !== undefined &&
              cEl.value.level !== undefined &&
              cEl.value.table !== undefined &&
              cEl.value.level === value.level &&
              cEl.value.table === value.table
        })
      }).length : data.length
    },
    reloadConfidence () {
      const initialSignificanceInterval = setInterval(async () => {
        const metricsLength = this.filteredData.length
        if (metricsLength > 0) {
          clearInterval(initialSignificanceInterval)
          await this.calculateConfidence()
        }
      }, 1000)
    },
    checkConfidenceDisplay (item, metric) {
      if (this.getConfidenceValue(item, metric) !== undefined) {
        return this.getConfidenceValue(item, metric).value.level !== '' &&
          this.getConfidenceValue(item, metric).value.value !== '' &&
          this.getMetricValue(item, metric) !== '-' &&
          metric.confidence === true
      } else {
        return false
      }
    },
    getRawMetricValue (item, metricKey) {
      if (!metricKey) return null
      const [metricValue, aggregation] = metricKey.split('.')
      if (!metricValue || !aggregation) return null
      const key = Object.keys(item).find(
        el => el.includes(metricValue) && el.includes(aggregation)
      )
      if (!key) return null
      const value = item[key]
      return this.parseChartNumber(value)
    }
  },
  async mounted () {
    this.fragranceColumnWidth = this.getDefaultFragranceColumnWidth()
    if (this.data && this.data.length && !Object.keys(this.selfFilters.selectedProducts).length) {
      this.data.forEach(fragrance => {
        this.selfFilters.selectedProducts[this.fragUniqueId(fragrance)] = true
      })
    }
    const xlsxFiltersResponse = await this.fetchXlsxFilters()
    this.xlsxFilters = xlsxFiltersResponse['xlsxFilters']
    this.chosenXlsxFilters = xlsxFiltersResponse['chosenXlsxFilters']
    if (this.metricTypes.some(metric => metric.confidence)) {
      this.reloadConfidence()
    }
    this.setFragrancesButtonText()
    this.syncFragranceColumnWidth(this.fragranceColumnWidth)
    window.addEventListener('resize', this.handleWindowResize)
  },
  beforeUnmount () {
    if (this.filterTimer) {
      clearTimeout(this.filterTimer)
      this.filterTimer = null
    }
    if (this.intervals.metrics) {
      clearInterval(this.intervals.metrics)
    }
    if (this.intervals.significance) {
      clearInterval(this.intervals.significance)
    }
    this.stopFragranceResize()
    window.removeEventListener('resize', this.handleWindowResize)
  },
  watch: {
    data (newData) {
      if (newData && !this.selfFilters.isFiltered) {
        this.selfFilters.selectedProducts = {}
        newData.forEach(fragrance => {
          this.selfFilters.selectedProducts[this.fragUniqueId(fragrance)] = true
        })
        this.showEmptyTable = false
      } else if (newData && this.selfFilters.isFiltered) {
        this.showEmptyTable = true
      } else if (!newData || (newData && !Object.keys(newData).length)) {
        this.selfFilters.isFiltered = false
      }
      this.setFragrancesButtonText()
      this.syncFragranceColumnWidth()
    },
    metricTypes () {
      if (this.metricTypes.some(metric => metric.confidence)) {
        this.reloadConfidence()
      }
      this.syncFragranceColumnWidth()
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/variables";

.table-container {
  position: relative;
  flex: 0 0 50%;
  max-width: 50%;
  min-width: 0;
  height: 1200px;
  overflow: hidden;
  box-sizing: border-box;
}

.table-container-expanded {
  position: relative;
  flex: 0 0 100%;
  max-width: 100%;
  min-width: 0;
  height: 1200px;
  overflow: hidden;
  box-sizing: border-box;
}

.select-group-item {
  margin: 0 !important;
  min-width: unset;
}

.metric-value {
  text-align: center;
  width: auto;
  min-width: 100px;
  padding: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.main-data {
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 40px;
  width: 100%;
  cursor: pointer;
}

.additional-data {
  display: flex;
  justify-content: space-around;
  align-items: center;
  overflow: hidden;
  width: 98%;
  cursor: pointer;
}

.search-input {
  position: absolute;
  right: 0;
}

.search-input:hover .input-group {
  width: 230px;
}

.search-input .input-group {
  transition: width 0.35s ease-in;
  flex-wrap: nowrap;
  overflow: hidden;
  width: 39px;
}

.no-data-input {
  position: absolute;
  right: 0;
}

.form-input {
  padding: 3px 10px;
  flex: 0 0 50%;
  max-width: 50%;
  @media screen and (min-width: $break-xl) {
    flex: 0 0 50%;
    max-width: 50%;
  }
  @media screen and (min-width: $break-xxl) {
    flex: 0 0 25%;
    max-width: 25%;
  }
}

.no-data-input:hover .input-group {
  width: 230px;
}

.no-data-input .input-group {
  transition: width 0.35s ease-in;
  flex-wrap: nowrap;
  overflow: hidden;
  width: 39px;
}

.data {
  border-bottom: 1px solid #d1d4d7;
  transition: 0.1s background-color linear;
  padding: 0 10px;
}

.data:hover {
  background-color: #f6f6f6;
}

.additional-data-hide {
  height: 0;
}

.additional-data-show {
  height: 50px;
  border-top: 1px dashed #d1d4d7;
}

.data-show {
  height: 130px;
}

.data-hide {
  height: 40px;
}

.scroller {
  overflow-x: visible;
  overflow-y: unset;
  height: calc(95% - 60px);
}

.tab-contents {
  margin-top: 5px;
  max-height: calc(100vh - 240px);
  overflow-y: auto;
  align-items: flex-start;
}

.column-header {
  text-align: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 0;
  transition: 0.1s background-color linear;
}

.table-header {
  z-index: 3;
  position: sticky;
  top: 0;
  background-color: white;
  border-bottom: 1px solid #d1d4d7;
}

.column-header:hover {
  background-color: #f6f6f6;
}

.column-body {
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
}

.file-data {
  width: 100%;
}

.file-data:hover {
  color: #20a8d8;
}

.file-icon {
  font-size: 1.5rem;
  float: right;
}

.no-data {
  max-height: calc(55vh - 4rem);
  min-height: 523px;
}

.results-box {
  flex: 0 0 50%;
  max-width: 50%;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  padding: 5px 5px 5px 21px;
  margin-top: 20px;
  box-sizing: border-box;
}

.no-hover:hover {
  background-color: unset;
}

.hide-input {
  display: none !important;
}

.download-button {
  z-index: 10;
  position: absolute;
  bottom: 10px;
  right: 10px;
}

.download-text {
  text-align: right;
  height: 20px;
  margin-left: 10px;
}

.centered {
  text-align: center;
}

.uncentered {
  text-align: unset !important;
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
}

.input-wrapper input {
  width: 100%;
}

.input-wrapper .el-select {
  width: 100%;
}

.input-wrapper label {
  font-size: 11px;
  color: $input-label;
  margin-bottom: 2px;
  margin-left: 2px;
}

.excel-download-button {
  width: 240px;
  height: 48px;
  background-color: $celery-500;
  border-radius: 4px;
  font-family: "Open Sans", sans-serif;
  font-weight: 900;
  font-size: 16px;
  line-height: 30px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: white;

  transition: background-color 0.2s linear;

  &:hover {
    color: white;
    background-color: $celery-700;
  }
}

.list-filter-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.calculate-confidence-wrapper {
  display: flex;
  flex-direction: row;
  margin-bottom: 10px;
}

.select-calculations {
  width: 100%;
}

.fragrance-name {
  position: relative;
  flex: 0 0 auto;
}

.fragrance-name-header {
  padding-right: 12px;
  user-select: none;
}

.fragrance-column-resizer {
  position: absolute;
  top: 0;
  right: -8px;
  width: 16px;
  height: 100%;
  cursor: col-resize;
  touch-action: none;
  z-index: 2;

  &::after {
    content: "";
    position: absolute;
    top: 8px;
    bottom: 8px;
    left: 50%;
    width: 2px;
    transform: translateX(-50%);
    background-color: #d1d4d7;
    border-radius: 999px;
  }
}

.fragrance-name-header:hover .fragrance-column-resizer::after {
  background-color: $blueberry;
}

.select-all-button {
  margin-right: 10px;
  width: 120px;
  min-width: 120px;
  height: 40px;
  background-color: $blueberry;
  border-radius: 4px;
  font-family: "Open Sans", sans-serif;
  font-weight: 900;
  font-size: 13px;
  line-height: 26px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: white;

  transition: background-color 0.2s linear;

  &:hover {
    background-color: #9256d9;
  }
}

.fragrances-container {
  height: calc(100% - 70px);
  min-width: 0;
  overflow-x: auto;
}

.metric-threshold {
  margin: 10px 0;

  &__label {
    margin: 0 10px 0 0;
    font-weight: 600;
  }

  &__select {
    min-width: 220px;
  }

  &__input {
    max-width: 110px;
  }

  &__clear {
    width: 90px;
  }
}

.fragrances-wrapper {
  height: 100%;
  max-width: none;
}

.charts-container {
  width: 100%;
  min-width: 0;
  margin: 20px 0;
  height: 815px;
  overflow-y: auto;
}

.results-header {
  display: flex;
  justify-content: space-between;
  margin-top: 0;
  padding: 20px 66px 10px;
  width: 100%;
  border-top: 1px solid #25232c;

  &__title {
    color: #545772;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: "Source Sans Pro", sans-serif;
    font-size: 24px;
    text-transform: uppercase;
    line-height: 28px;
    letter-spacing: 0;
    font-weight: 900;
  }
}

.tag {
  background-color: $blueberry;
  border-color: $blueberry;
}

.expand-button {
  margin-left: 10px;
  margin-right: auto;
  width: 120px;
  min-width: 120px;
  height: 40px;
  background-color: $blueberry;
  border-radius: 4px;
  font-family: "Open Sans", sans-serif;
  font-weight: 900;
  font-size: 13px;
  line-height: 30px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: white;

  transition: background-color 0.2s linear;

  &:hover {
    background-color: #9256d9;
  }
}

.age-switch {
  --el-switch-on-color: #300296;
  --el-switch-off-color: #e8eaea;
}
</style>
<style lang="scss">
.checkbox-tooltip {
  left: 30px !important;
}
</style>
